import React, { useContext, useEffect, useMemo, useState } from 'react';
import { assign } from 'xstate';
import { cloneDeep } from 'lodash';
import { IntlShape } from 'react-intl';
import {
  ImageListItem,
  ImageListItemBar,
  Checkbox,
  Box,
  ImageList,
} from '@mui/material';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';

import { MachineContext } from '../../MachineContext';
import { BaseComponent } from '../Component';
import { translateWithFallback } from '../../util';
import { SendContext } from '../../SendContext';
import { theme } from '../../theme/theme';
import { EvoachButton } from '../Button';
import { ComponentSize } from '../ComponentSizeEnum';

export interface ImageSelectComponent extends BaseComponent {
  type: 'imageSelect';
  props: ImageSelectProps;
}

export interface ImageItem {
  type: string;
  value: string;
  src: string;
  selected?: boolean;
  resolvedsrc: string;
  assetid: string;
}

export interface ImageSelectProps {
  images: ImageItem[];
  componentSize?: ComponentSize;
  maxItems?: number;
  minItems?: number;
  displayItems?: number;
  showTitles?: boolean;
  saveResultTo: string;
  buttonText: string;
  isEcho?: boolean;
}

const selectedItems = (list: ImageItem[]): number => {
  let result = 0;
  list.forEach((value) => {
    if (value.selected) result++;
  });
  return result;
};

export const ImageSelect: React.FC<ImageSelectProps> = ({
  images,
  componentSize,
  maxItems = 1,
  minItems = 1,
  //displayItems = 1,
  showTitles = false,
  saveResultTo,
  buttonText,
  isEcho = false,
}) => {
  const colNumbers =
    componentSize === ComponentSize.LARGE
      ? 1
      : componentSize === ComponentSize.MEDIUM
      ? 2
      : 3;

  const { send } = useContext(SendContext);
  const imageSelectID = useMemo(() => {
    return 'imageSelect' + isEcho + Math.round(Math.random() * 100000000);
  }, [isEcho]);

  const [items, setItems] = useState<ImageItem[]>(images ?? []);

  // if images changes, we have to update the state. This happens when
  // the component is used in a .node file in the Creator app. It should
  // never happen when using the component in Player app
  useEffect(() => {
    setItems(images ?? []);
  }, [images]);

  // removed this adaptations: ?w=${width}&fit=crop&auto=format
  // from src and srcSet because it is not working with S3 pre-signed
  // URLs that are used for assets

  return (
    <div key={imageSelectID + 'surroundingDiv'}>
      <Box
        sx={{
          display: 'flex',
          gap: '10px',
          flexWrap: 'wrap',
          marginBottom: '10px',
        }}
      >
        <ImageList cols={colNumbers} variant="standard">
          {items &&
            items.map((item, index) => {
              return (
                <ImageListItem
                  key={index}
                  sx={{
                    cursor: 'pointer',
                    overflow: 'hidden',
                  }}
                  onClick={() => {
                    if (!isEcho) {
                      const newItems = cloneDeep(items);
                      newItems[index].selected = !newItems[index].selected;
                      setItems(newItems);
                    }
                  }}
                >
                  <img
                    style={{
                      alignSelf: 'center',
                    }}
                    src={`${item.resolvedsrc}`}
                    alt={item.value}
                    loading="lazy"
                  />
                  <ImageListItemBar
                    sx={{
                      background:
                        'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, ' +
                        'rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                    }}
                    title={showTitles ? item.value : ''}
                    position="top"
                    actionIcon={
                      <Checkbox
                        checkedIcon={
                          <StarIcon
                            sx={{ color: theme.palette.secondary.main }}
                          />
                        }
                        icon={
                          <StarBorderIcon
                            sx={{ color: theme.palette.secondary.contrastText }}
                          />
                        }
                        checked={!!item.selected}
                      />
                    }
                    actionPosition="left"
                  />
                </ImageListItem>
              );
            })}
        </ImageList>
      </Box>
      {!isEcho && (
        <EvoachButton
          disabled={
            (minItems !== undefined && minItems > selectedItems(items)) ||
            (maxItems !== undefined && maxItems < selectedItems(items))
          }
          onClick={() => {
            send({
              type: 'writeToContext',
              payload: {
                [saveResultTo]: items.filter((item) => item.selected),
              },
            });
            send({
              type: 'writeToContext',
              payload: {
                [saveResultTo + '_ALL']: items,
              },
            });
            send({ type: 'next' });
          }}
        >
          {buttonText}
        </EvoachButton>
      )}
    </div>
  );
};

export const renderImageSelectAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;

    // init with images if available;
    let images: ImageItem[] = payload.images
      ? payload.images.map((img: ImageItem) => {
          return { ...img, value: translateWithFallback(intl, img.value) };
        })
      : [];

    // if there is a getValueFrom (e.g. for isEcho / Display render), try to get data
    // from there.
    if (payload.getValueFrom !== undefined && payload.getValueFrom !== '') {
      // translate value, leave all other elements untouched
      const data = context.userData[payload.getValueFrom];
      if (data !== undefined && Array.isArray(data)) {
        images = data;
      }
    }

    newContext.widgetData.push({
      type: 'imageSelect',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        images: images,
        componentSize: payload.componentSize,
        minItems: payload.minItems,
        maxItems: payload.maxItems,
        displayItems: payload.displayItems,
        showTitles: payload.showTitles,
        saveResultTo: payload.saveResultTo,
        isEcho: payload.isEcho,
        buttonText: translateWithFallback(intl, payload.buttonText),
      },
    });

    return newContext;
  });
};
